import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicTransitApi } from '@api/publicTransitApi';
import { isErrorResponse } from '@utils/utils';
import { BaseEntityNameLess } from '@api/entities/BaseEntity';
import {
    RouteEntity,
    ScheduleItem,
} from '@features/public-transit-editor/transit-management/TransitStops/models/types';

export const useStopInfo = () => {
    const { stopId } = useParams();
    const [loading, setLoading] = useState(true);
    const [stop, setStop] = useState<any>(null);
    const [routes, setRoutes] = useState<RouteEntity[]>([]);
    const [departures, setDepartures] = useState<any[]>([]);

    const loadData = useCallback(async () => {
        if (!stopId) {
            return;
        }

        setLoading(true);
        const abortController = new AbortController();

        try {
            const stopInfo = await publicTransitApi.getStopInfo({ stopId }, abortController.signal);

            if (!isErrorResponse(stopInfo)) {
                setStop({
                    id: stopId,
                    name: stopInfo.name,
                });
                setRoutes(
                    (stopInfo.routes || []).map((r: any, index: number) => ({
                        id: r.id,
                        name: r.routeName,
                        color: r.color,
                        departures: r.departuresCount,
                        index,
                    }))
                );
                setDepartures(stopInfo.departures || []);
            }
        } catch (e) {
            console.error('Replan. TransitStopInfo loading error:', e);
        } finally {
            setLoading(false);
        }
    }, [stopId]);

    useEffect(() => {
        void (async function () {
            await loadData();
        })();
    }, [loadData]);

    const onRouteClick = useCallback(async (entity: BaseEntityNameLess) => {
        return;
    }, []);

    const routeIndexByName = useMemo(() => {
        const map = new Map<string, number>();
        routes.forEach((route, index) => {
            const key = route.name ?? route.id;
            if (!key) {
                return;
            }
            map.set(key, route.index ?? index);
        });
        return map;
    }, [routes]);

    const schedule = useMemo<ScheduleItem[]>(() => {
        return departures.map(d => ({
            time: d.startTime,
            routeName: d.routeName,
            color: d.routeColor,
            routeIndex: routeIndexByName.get(d.routeName),
        }));
    }, [departures, routeIndexByName]);

    return {
        loading,
        stop,
        routes,
        schedule,
        onRouteClick,
    };
};
